// src/components/overlay/EntryOverlay.jsx
import React from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Modal,
  Stack,
  Typography,
} from '@mui/material';
import CasinoIcon from '@mui/icons-material/Casino';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SportsKabaddiIcon from '@mui/icons-material/SportsKabaddi';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import { keyframes } from '@mui/system';
import { useNavigate } from 'react-router-dom';

const casinoGlow = keyframes`
  0%, 100% {
    box-shadow:
      0 0 10px rgba(255, 215, 0, 0.65),
      0 0 22px rgba(255, 0, 128, 0.35);
    transform: scale(1);
  }
  50% {
    box-shadow:
      0 0 20px rgba(255, 235, 59, 1),
      0 0 42px rgba(255, 0, 128, 0.75);
    transform: scale(1.015);
  }
`;

const newBadgePulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.12); opacity: 0.82; }
`;

export default function EntryOverlay({
  open,
  onClose,
  mediaSrc = 'https://res.cloudinary.com/dekoxefky/video/upload/v1761188757/Mi_video_1_pyhe4b.mp4',
  mediaAlt = 'Big Dog Bone Arena',
  casinoRoute = '/game/casino',
  burnRoute = '/game/album?page=6',
  arenaRoute = '/game/arena',
  rankingRoute = '/game/album?page=7',
  miningRoute = '/game/album?page=8',
}) {
  const navigate = useNavigate();

  const go = (path) => {
    if (!path) return;
    onClose?.();
    navigate(path, { replace: false });
  };

  const isVideo = /\.(mp4|webm|ogg)(\?.*)?$/i.test(mediaSrc || '');

  const baseButtonSx = {
    position: 'relative',
    minHeight: 46,
    py: 1.15,
    px: 1.5,
    borderRadius: 2.5,
    overflow: 'hidden',
    color: '#fff',
    fontWeight: 900,
    textTransform: 'none',
    fontSize: { xs: '0.82rem', sm: '0.9rem' },
    letterSpacing: 0.2,
    border: '2px solid rgba(255, 225, 145, 0.82)',
    boxShadow: '0 0 12px rgba(0,0,0,0.78)',
    textShadow: '0 1px 4px rgba(0,0,0,0.9)',
    transition: 'transform 180ms ease, box-shadow 180ms ease, filter 180ms ease',
    '&:hover': {
      transform: 'translateY(-2px) scale(1.015)',
      boxShadow: '0 0 24px rgba(255,213,79,0.95)',
      filter: 'brightness(1.08)',
    },
    '&:focus-visible': {
      outline: '3px solid rgba(255,255,255,0.9)',
      outlineOffset: 2,
    },
  };

  return (
    <Modal
      open={Boolean(open)}
      onClose={onClose}
      aria-labelledby="big-dog-bone-entry-title"
      aria-describedby="big-dog-bone-entry-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '92vw', sm: 620 },
          maxWidth: 620,
          maxHeight: '92vh',
          bgcolor: 'rgba(16, 16, 23, 0.98)',
          color: '#fff',
          borderRadius: 3,
          overflowX: 'hidden',
          overflowY: 'auto',
          border: '1px solid rgba(255,255,255,0.16)',
          boxShadow: '0 24px 80px rgba(0,0,0,0.78)',
          outline: 'none',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 7',
            minHeight: { xs: 145, sm: 210 },
            bgcolor: '#000',
            overflow: 'hidden',
          }}
        >
          {isVideo ? (
            <video
              src={mediaSrc}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              aria-label={mediaAlt}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <img
              src={mediaSrc}
              alt={mediaAlt}
              loading="eager"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          )}

          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, rgba(0,0,0,0.05) 35%, rgba(0,0,0,0.9) 100%)',
              pointerEvents: 'none',
            }}
          />

          <IconButton
            onClick={onClose}
            aria-label="Close welcome menu"
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.58)',
              border: '1px solid rgba(255,255,255,0.2)',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.82)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Typography
            id="big-dog-bone-entry-title"
            variant="h6"
            sx={{
              position: 'absolute',
              left: 14,
              right: 54,
              bottom: 10,
              fontWeight: 950,
              fontSize: { xs: '1rem', sm: '1.18rem' },
              textShadow: '0 2px 12px rgba(0,0,0,1)',
            }}
          >
            Welcome to Big Dog Bone!
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 1.6, sm: 2.2 } }}>
          <Typography
            id="big-dog-bone-entry-description"
            variant="body2"
            sx={{
              opacity: 0.92,
              fontSize: { xs: '0.78rem', sm: '0.86rem' },
              lineHeight: 1.4,
            }}
          >
            🌟 The Big Dog Bone universe is growing with new adventures,
            competitive modes, bigger rewards and five HUESO casino games.
          </Typography>

          <Divider
            sx={{
              my: 1.5,
              borderColor: 'rgba(255,255,255,0.1)',
            }}
          />

          <Stack spacing={1.05}>
            <Button
              fullWidth
              onClick={() => go(casinoRoute)}
              startIcon={<CasinoIcon />}
              sx={{
                ...baseButtonSx,
                minHeight: 56,
                background:
                  'linear-gradient(115deg, #5f0a87 0%, #d40078 42%, #ff8f00 72%, #ffd740 100%)',
                animation: `${casinoGlow} 1.35s ease-in-out infinite`,
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  width: '100%',
                }}
              >
                <Box component="span">🎰 HUESO CASINO — 5 GAMES</Box>
                <Box
                  component="span"
                  sx={{
                    px: 0.75,
                    py: 0.2,
                    borderRadius: 999,
                    bgcolor: '#ff1744',
                    color: '#fff',
                    fontSize: '0.64rem',
                    lineHeight: 1.4,
                    animation: `${newBadgePulse} 0.9s ease-in-out infinite`,
                    '@media (prefers-reduced-motion: reduce)': {
                      animation: 'none',
                    },
                  }}
                >
                  NEW
                </Box>
              </Box>
            </Button>

            <Button
              fullWidth
              onClick={() => go(burnRoute)}
              startIcon={<WhatshotIcon />}
              sx={{
                ...baseButtonSx,
                background:
                  'linear-gradient(135deg, #ef6c00, #ff9800, #ffca28)',
              }}
            >
              Burn NFTs and get PP
            </Button>

            <Button
              fullWidth
              onClick={() => go(arenaRoute)}
              startIcon={<SportsKabaddiIcon />}
              sx={{
                ...baseButtonSx,
                background:
                  'linear-gradient(135deg, #7b1fa2, #ab47bc, #ce93d8)',
              }}
            >
              Enter Arena
            </Button>

            <Button
              fullWidth
              onClick={() => go(rankingRoute)}
              startIcon={<EmojiEventsIcon />}
              sx={{
                ...baseButtonSx,
                background:
                  'linear-gradient(135deg, #2e7d32, #43a047, #9ccc65)',
              }}
            >
              View Ranking
            </Button>

            <Button
              fullWidth
              onClick={() => go(miningRoute)}
              startIcon={<EmojiEventsIcon />}
              sx={{
                ...baseButtonSx,
                background:
                  'linear-gradient(135deg, #3949ab, #5c6bc0, #42a5f5)',
              }}
            >
              Mining Mode
            </Button>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
}
